import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormGroup from '@mui/material/FormGroup'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import CheckboxField from '@/components/formik/fields/CheckboxField'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'

import { ConsultationType } from '@/core/models/consultation.model'

export type ConsultationsSearchFormValues = {
  doctor: string
  patient: string
  type: ConsultationType[]
  startDate: {
    $gte: Date | null
    $lte: Date | null
  }
}

export type ConsultationsSearchFormProps = {
  consultationTypes: ConsultationType[]
  direction?: 'column' | 'row'
  doctorOptions: AutocompleteOption[]
  initialValues: ConsultationsSearchFormValues
  onReset?: (values: ConsultationsSearchFormValues) => void
  onSearchDoctor?: (value: string) => void
  onSearchPatient?: (value: string) => void
  onSubmit: (values: ConsultationsSearchFormValues) => void
  patientOptions: AutocompleteOption[]
}

export default function ConsultationsSearchForm(props: ConsultationsSearchFormProps) {
  const { consultationTypes, direction = 'column', doctorOptions, initialValues, onReset, onSearchDoctor, onSearchPatient, onSubmit, patientOptions } = props
  const { t } = useTranslation()

  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))

  const handleSearchDoctor = useCallback(
    (inputValue: string) => {
      onSearchDoctor?.(inputValue)
    },
    [onSearchDoctor]
  )

  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  return (
    <Box sx={{ padding: '0 15px' }} id="consultation-search-form">
      <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
        {t('page.consultations.consultationsSearchForm.title')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormGroup row={isLargeScreen? false:true}>
                  {consultationTypes.map((type, index) => (
                    <CheckboxField
                      key={`${type}${index}`}
                      label={t(`page.consultations.consultationsSearchForm.field.searchByType.option.${type}.label`)}
                      name="type"
                      value={type}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <AutocompleteField
                  label={t('page.consultations.consultationsSearchForm.field.searchByPatient.label')}
                  name="patient"
                  onInputChange={handleSearchPatient}
                  options={patientOptions}
                />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <AutocompleteField
                  label={t('page.consultations.consultationsSearchForm.field.searchByDoctor.label')}
                  name="doctor"
                  onInputChange={handleSearchDoctor}
                  options={doctorOptions}
                />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateFrom.label')} name="startDate.$gte" />
              </Grid>
              <Grid item xs={12} sm={isLargeScreen ? 12 : 6}>
                <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateTo.label')} name="startDate.$lte" />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="end" spacing={1}>
                  <Button
                    onClick={() => {
                      formikProps.handleReset()
                      onReset?.(formikProps.initialValues)
                    }}
                    variant="neutral"
                  >
                    {t('common:button.reset.label')}
                  </Button>
                  <LoadingButton id="searchButton" type="submit" variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
